import { useEffect, useState } from 'react';
import { ApiService } from 'services/ApiService';

export interface ArtistFollowButtonProps {
  artistId: string;
  isVisitor: boolean;
}

const ArtistFollowButton: React.FC<ArtistFollowButtonProps> = ({ artistId, isVisitor }) => {
  const [isFollowing, setIsFollowing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!artistId || !isVisitor) return;

    const load = async () => {
      try {
        const res = await ApiService.getIsFollowingArtist(artistId);
        if (res.success && res.data !== undefined) {
          setIsFollowing(res.data.isFollowing);
        }
      } catch {
        // Non-blocking: if endpoint isn't available yet, default to not-following.
      }
    };

    load();
  }, [artistId, isVisitor]);

  if (!artistId || !isVisitor) return null;

  const toggleFollow = async () => {
    if (isLoading) return;

    setIsLoading(true);
    const next = !isFollowing;
    setIsFollowing(next);

    try {
      if (next) {
        await ApiService.followArtist(artistId);
      } else {
        await ApiService.unfollowArtist(artistId);
      }
    } catch (e) {
      // Revert optimistic update
      setIsFollowing(!next);
      console.error('Failed to toggle follow:', e);
      alert('Could not update follow status. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={toggleFollow}
        disabled={isLoading}
        aria-label={isFollowing ? 'Unfollow artist' : 'Follow artist'}
      className={`inline-flex items-center justify-center rounded-md px-3 py-2 text-sm border transition duration-200 ${
        isFollowing
          ? 'bg-red-600 text-white border-red-600 hover:bg-red-700'
          : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'
      } ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
    >
      <span className="text-base leading-none mr-2">{isFollowing ? '♥' : '♡'}</span>
      {isFollowing ? 'Following' : 'Follow'}
    </button>
  </>
);
};

export default ArtistFollowButton;
